import styled from '@emotion/styled';
import React from 'react';
import { Checkbox, Css, ITheme, Responsive } from '../../../../../ui';
import { Input } from '../../../../../components/common/input/Input';
import Button from '../../../../../components/common/button/Button';
import { QuickLink } from '../../../../../components/common/quick-links/QuickLink';
import { Color } from '../../../../../styles/Color';
import { SUBSCRIPTIONS_API, SOLICITATION_API } from '../../../domain/api';
import { ISolicitationEntry } from '../../../../../contentful/types/ISolicitationEntry';
import { useSolicitationForm } from './helpers/useSolicitationForm';
import { useApi } from '../../../../../data/hooks/useApi';
import { PageRoutes } from '../../../../app/pages/domain/constants/PageRoutes';
import { ISolicitationPostData } from '../../../domain/models/ISolicitationPostData';
import { ErrorMessages } from '../../../../../utils/errorMessages';
import { jsonToBase64 } from '../../../../../utils/jsonToBase64';

interface ISolicitationForm {
	solicitation: ISolicitationEntry;
}

interface ISolicitationFormData {
	firstName: string;
	lastName: string;
	email: string;
	company?: string;
	phone?: string;
	mwbe_dbe?: boolean;
}

const API_KEY = process.env.HAS_SOLICITATIONS_API_KEY;

export default function SolicitationForm({ solicitation }: ISolicitationForm) {
	const { title, solicitationNumber, slug } = solicitation.fields;
	const {
		handleSubmit,
		register,
		formState: { errors },
		reset,
	} = useSolicitationForm();

	const { loading, error, handleApiData } = useApi();

	const onSubmit = (data: ISolicitationFormData) => {
		const isSuccess = !loading && !error;

		const base64 = jsonToBase64({ id: solicitation.id });

		const subscriptionsPutUrl = `${SUBSCRIPTIONS_API}/${data.email}/solicitation/${base64}?platform=email`;
		handleApiData({ url: subscriptionsPutUrl, method: 'PUT' });

		if (isSuccess) {
			reset();
		}

		const solicitationPostData: ISolicitationPostData = {
			solicitation_id: solicitationNumber,
			contentful_id: solicitation.id,
			solicitation_title: title,
			mwbe_dbe: data.mwbe_dbe ? 1 : 0,
			phone: data.phone,
			first_name: data.firstName,
			last_name: data.lastName,
			email: data.email,
			company: data.company,
		};

		handleApiData({
			url: SOLICITATION_API,
			method: 'POST',
			body: solicitationPostData,
			headers: { 'x-api-key': `${API_KEY}` },
		});
	};

	return (
		<>
			<FormDescription>
				To receive email notifications about changes or addendums to this
				posting, you must register as a Prospective Proposer. Registration is
				specific to this contract opportunity. The Prospective Proposers List
				and the information you provide will be publicly displayed on this
				website. Click below to join the list and receive email notifications of
				new documents for this opportunity.
			</FormDescription>

			<StyledForm onSubmit={handleSubmit(onSubmit)} role="form">
				<StyledInput
					label="First Name*"
					{...register('firstName')}
					isInvalid={!!errors.firstName}
				/>
				{errors.firstName && <FormError>{errors.firstName.message}</FormError>}

				<StyledInput
					label="Last Name*"
					{...register('lastName')}
					isInvalid={!!errors.lastName}
				/>
				{errors.lastName && <FormError>{errors.lastName.message}</FormError>}

				<StyledInput
					label="Email*"
					type="text"
					{...register('email')}
					isInvalid={!!errors.email}
				/>
				{errors.email && <FormError>{errors.email.message}</FormError>}

				<StyledInput
					label="Company"
					{...register('company')}
					isInvalid={!!errors.company}
				/>
				{errors.company && <FormError>{errors.company.message}</FormError>}

				<StyledInput
					label="Phone"
					type="tel"
					{...register('phone')}
					isInvalid={!!errors.phone}
				/>
				{errors.phone && <FormError>{errors.phone.message}</FormError>}

				<Checkbox label="MWBE/DBE" {...register('mwbe_dbe')} />
				{errors.mwbe_dbe && <FormError>{errors.mwbe_dbe.message}</FormError>}

				<FormButtonWrapper>
					<QuickLink
						type="internal"
						url={`${PageRoutes.CorporateSolicitationsDetails}/${slug}/proposers`}
					>
						List of Current Prospective Proposers
					</QuickLink>

					<ButtonWrapper>
						<Button>Submit</Button>
						{error && (
							<FormSubmitError>{ErrorMessages.GenericError2}</FormSubmitError>
						)}
					</ButtonWrapper>
				</FormButtonWrapper>
			</StyledForm>
		</>
	);
}

const FormDescription = styled.p`
	margin-bottom: ${Css.pxToRem(40)};
`;

const StyledInput = styled(Input)<{}, ITheme>`
	max-width: ${({ theme }) => theme.layout.main.textMaxWidth};
	padding-left: ${Css.pxToRem(16)};
	color: ${Color.OnBackground};
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		gap: ${Css.pxToRem(16)};
	}

	label {
		left: ${Css.pxToRem(16)};
		color: ${Color.Neutral6};
	}
`;

const FormButtonWrapper = styled.div<{}, ITheme>`
	display: flex;
	justify-content: space-between;
	max-width: ${({ theme }) => theme.layout.main.textMaxWidth};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(20)};
	}
`;

const FormError = styled.p`
	color: ${Color.Error};
	margin-bottom: 0;
	margin-top: ${Css.pxToRem(-18)};
	font-weight: 500;
	font-size: ${Css.pxToRem(12)};
`;

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	button {
		max-width: ${Css.pxToRem(167)};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			max-width: 100%;
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		align-items: unset;
	}
`;

const FormSubmitError = styled.div`
	color: ${Color.Error};
	margin-top: ${Css.pxToRem(8)};
	font-weight: 500;
	font-size: ${Css.pxToRem(14)};
`;
